import DataRepo from '@api/datasource/data'
import QueryKeys from '@constants/queryKeys'
import {
  ClientBaseType,
  GetClientsType,
  StatusClientType,
  UpdateClientType,
} from '@customTypes/client'
import { ActionIcon, Badge, Box, Container, Paper, ScrollArea, Text, Title } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ReactSortable } from 'react-sortablejs'

import lodash from 'lodash'
import React from 'react'
import { transformColor } from '@utils/theme'
import { notifications } from '@mantine/notifications'
import queryClient from '@api/datasource/query'
import { debounceUpdate } from '@utils/network'
import {
  IconEdit,
  IconPlus,
  IconTimelineEventPlus,
  IconTrash,
  IconUserEdit,
} from '@tabler/icons-react'
import { ModalFormType } from '@customTypes/page'
import StatusForm from '@components/client/statusForm'
import ClientForm, { ClientFormDataType } from '@components/client/form'
import ConfirmationModal from '@components/shared/confirmationModal'
import ClientPreview from '@components/client/preview'
import EventForm, { EventFormType } from '@components/event/form'
import { ClientTypeOptions } from '@constants/client'
import Input from '@components/shared/input'
import { buildSelectOptions } from '@utils/form'
import { onFilterClient } from '@utils/client'
import { useParams } from '@hooks/params'
import { useLocalStorage } from '@mantine/hooks'

type GroupClient = StatusClientType & {
  clients: ClientBaseType[]
}

const Dashboard = () => {
  const statusQuery = useQuery({
    placeholderData: [],
    queryKey: [QueryKeys.GET_STATUS_KEY],
    queryFn: () => DataRepo.clientEntityService.getStatus(),
  })

  const [statusForm, setStatusForm] = React.useState<ModalFormType<StatusClientType>>({
    open: false,
  })

  const [clientForm, setClientForm] = React.useState<ModalFormType<ClientFormDataType>>({
    open: false,
  })

  const [clientDelete, setClientDelete] = React.useState<ModalFormType<ClientBaseType>>({
    open: false,
  })

  const [clientPreview, setClientPreview] = React.useState<ModalFormType<ClientBaseType>>({
    open: false,
  })
  const [modalEventForm, setEventForm] = React.useState<ModalFormType<EventFormType>>({
    open: false,
  })

  const [groupedData, setGroupedData] = React.useState<GroupClient[]>([])

  const [params] = useParams<GetClientsType>({
    queryKey: QueryKeys.GET_CLIENTS_KEY,
    initialParams: {},
  })

  const clientsQuery = useQuery({
    enabled: !!statusQuery.data?.length,
    queryKey: [QueryKeys.GET_CLIENTS_KEY],
    queryFn: async () => {
      const response = await DataRepo.clientEntityService.getClients({})
      return response
    },
  })

  const [clientType, setClientType] = useLocalStorage({
    key: 'clientType-dashboard',
    defaultValue: null as 'prospect' | 'client' | null,
    getInitialValueInEffect: true,
    sync: true,
  })

  const memoFilterClient = React.useMemo(() => {
    const results = onFilterClient({
      data: clientsQuery.data || [],
      params,
    })

    return results.filter((item) => {
      if (!clientType) return true

      return item.type === clientType
    })
  }, [clientsQuery.data, params, clientType])

  const clientStatusMutation = useMutation<void, Error, UpdateClientType>({
    mutationFn: (data) => {
      return DataRepo.clientEntityService.updateClient(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_CLIENTS_KEY],
        refetchType: 'all',
      })
    },
    onError: (error) => {
      notifications.show({
        title: 'Error al actualizar el cliente',
        message: error.message || 'Ocurrió un error al actualizar el cliente.',
        color: 'red',
      })
    },
  })

  const clientDeleteMutation = useMutation<void, Error, string>({
    mutationFn: (id) => {
      return DataRepo.clientEntityService.deleteClient(id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.GET_CLIENTS_KEY],
        refetchType: 'all',
      })
      notifications.show({
        title: 'Cliente eliminado',
        message: 'El cliente ha sido eliminado correctamente.',
        color: 'green',
      })
    },
    onError: (error) => {
      notifications.show({
        title: 'Error al eliminar el cliente',
        message: error.message || 'Ocurrió un error al eliminar el cliente.',
        color: 'red',
      })
    },
  })

  const groupedFromQuery = React.useMemo(() => {
    if (!statusQuery.data?.length) return []

    const objectGroup = lodash.groupBy(
      memoFilterClient,
      (client) => client.status?.uid || 'unknown',
    )

    const arrayGroups: GroupClient[] = statusQuery.data.map((status) => ({
      uid: status.uid,
      name: status.name,
      color: status.color,
      order: status.order,
      createdAt: status.createdAt,
      updatedAt: status.updatedAt,
      clients: objectGroup[status.uid] || [],
    }))

    arrayGroups.push({
      uid: 'unknown',
      name: 'Sin estado',
      color: '#ccc',
      order: 9999,
      createdAt: Date.now(),
      updatedAt: Date.now(),
      clients: objectGroup['unknown'] || [],
    })

    return arrayGroups.sort((a, b) => {
      if (a.name === 'Sin estado') return 1
      if (b.name === 'Sin estado') return -1
      return a.order - b.order
    })
  }, [memoFilterClient, statusQuery.data])

  React.useEffect(() => {
    setGroupedData(groupedFromQuery)
  }, [groupedFromQuery])

  return (
    <Container
      fluid
      className="cd-px-0 cd-grow cd-flex cd-flex-col cd-h-full cd-w-full cd-mt-[1rem] md:cd-mt-[2rem]"
    >
      <div className="cd-mb-4">
        <Input
          clearable
          searchable
          data={buildSelectOptions({
            data: Object.values(ClientTypeOptions),
            value: 'value',
            label: 'label',
          })}
          label="Tipo de cliente"
          placeholder="Mostrar clientes por tipo"
          typeInput="select"
          value={clientType}
          onChange={(value) => {
            setClientType(value as 'prospect' | 'client' | null)
          }}
        />
      </div>

      <ScrollArea.Autosize className="cd-h-full cd-grow">
        <div className="cd-mb-4 cd-flex cd-flex-row cd-gap-4">
          {groupedData?.map((group, idx) => (
            <Paper
              bg={transformColor({
                color: group.color,
                shift: 0.25,
                alpha: 0.2,
              })}
              className="!cd-p-2 cd-rounded-lg cd-grow cd-w-[300px]"
              key={`group-${idx}-${group.uid}`}
            >
              <Box className="cd-flex cd-items-center cd-justify-between cd-rounded-tl-lg cd-rounded-tr-lg">
                <Title
                  bg={group.color}
                  className="!cd-text-lg !cd-font-semibold !cd-text-white cd-px-3 cd-rounded-md"
                >
                  {group.name}
                </Title>
                <div className="cd-flex cd-items-center cd-justify-end cd-gap-2">
                  <ActionIcon
                    color={transformColor({
                      color: group.color,
                      shift: -0.1,
                    })}
                    variant="transparent"
                    onClick={() => setStatusForm({ open: true, data: group })}
                  >
                    <IconEdit />
                  </ActionIcon>
                  <ActionIcon
                    color={transformColor({
                      color: group.color,
                      shift: -0.1,
                    })}
                    variant="transparent"
                    onClick={() => setClientForm({ open: true, data: { initialStatus: group } })}
                  >
                    <IconPlus />
                  </ActionIcon>
                </div>
              </Box>
              <ScrollArea.Autosize className="cd-mt-2" mah={600}>
                <ReactSortable
                  animation={200}
                  className="cd-flex cd-flex-col cd-gap-2 cd-min-h-[150px] cd-w-full"
                  delay={2}
                  group={{ name: 'shared', pull: true, put: true }}
                  id={group.uid}
                  list={group.clients.map((client) => ({
                    id: client.uid,
                    name: client.name,
                  }))}
                  setList={() => null}
                  onAdd={handleAdd}
                >
                  {group.clients.map((client) => (
                    <Paper
                      withBorder
                      className="cd-py-2 cd-px-4 cd-rounded-md cd-cursor-pointer"
                      data-id={client.uid}
                      key={client.uid}
                      onClick={(e) => {
                        e.stopPropagation()
                        setClientPreview({ open: true, data: client })
                      }}
                    >
                      <div className="cd-flex cd-flex-col cd-gap-6">
                        <div className="cd-flex cd-flex-col cd-gap-2">
                          <Text>{client.name}</Text>

                          <Badge color={ClientTypeOptions[client.type]?.color || 'gray'}>
                            {ClientTypeOptions[client.type]?.label || 'Sin tipo'}
                          </Badge>
                        </div>

                        <div className="cd-flex cd-flex-row cd-items-center cd-justify-start cd-gap-4">
                          <ActionIcon
                            color="gray"
                            size="md"
                            variant="light"
                            onClick={(e) => {
                              e.stopPropagation()
                              setEventForm({
                                open: true,
                                data: {
                                  clientID: client.uid,
                                },
                              })
                            }}
                          >
                            <IconTimelineEventPlus size={20} />
                          </ActionIcon>
                          <ActionIcon
                            color="gray"
                            size="md"
                            variant="light"
                            onClick={(e) => {
                              e.stopPropagation()
                              setClientForm({ open: true, data: { client } })
                            }}
                          >
                            <IconUserEdit size={20} />
                          </ActionIcon>
                          <ActionIcon
                            color="red"
                            variant="light"
                            onClick={(e) => {
                              e.stopPropagation()
                              setClientDelete({
                                open: true,
                                data: client,
                              })
                            }}
                          >
                            <IconTrash size={20} />
                          </ActionIcon>
                        </div>
                      </div>
                    </Paper>
                  ))}
                </ReactSortable>
              </ScrollArea.Autosize>
            </Paper>
          ))}
        </div>
      </ScrollArea.Autosize>

      <StatusForm
        modal
        data={statusForm.data}
        open={statusForm.open}
        size="lg"
        onClose={() => setStatusForm({ open: false })}
      />

      <ClientForm
        modal
        data={clientForm.data}
        open={clientForm.open}
        size="70%"
        onClose={() => setClientForm({ open: false })}
      />

      <ClientPreview
        modal
        data={clientPreview.data}
        open={clientPreview.open}
        size="60%"
        onClose={() => setClientPreview({ open: false })}
      />

      <EventForm
        modal
        data={modalEventForm.data}
        open={modalEventForm.open}
        size="lg"
        onClose={() => setEventForm({ open: false })}
      />

      <ConfirmationModal
        cancelColor="gray"
        cancelText="Cancelar"
        confirmColor="red"
        confirmText="Eliminar"
        open={clientDelete.open}
        title="Eliminar Cliente"
        onCancel={() => setClientDelete({ open: false })}
        onConfirm={() => {
          if (clientDelete.data?.uid) {
            clientDeleteMutation.mutate(clientDelete.data.uid)
          }
          setClientDelete({ open: false })
        }}
      >
        <Text>
          ¿Estás seguro de que deseas eliminar el cliente <strong>{clientDelete.data?.name}</strong>
          ? Esta acción no se puede deshacer.
        </Text>
      </ConfirmationModal>
    </Container>
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function handleAdd(evt: any) {
    const fromGroupUid = evt.from.getAttribute('id') as string | null
    const toGroupUid = evt.to.getAttribute('id') as string | null
    const itemId = evt.item.dataset.id as string | null

    if (!fromGroupUid || !toGroupUid || !itemId) {
      console.error('Missing group or item information')
      return
    }

    setGroupedData((prevGroups) => {
      const sourceGroup = prevGroups.find((g) => g.uid === fromGroupUid)
      const targetGroup = prevGroups.find((g) => g.uid === toGroupUid)

      if (!sourceGroup || !targetGroup) return prevGroups

      const itemIndex = sourceGroup.clients.findIndex((c) => c.uid === itemId)

      if (itemIndex === -1) return prevGroups

      const [movedItem] = sourceGroup.clients.splice(itemIndex, 1)

      targetGroup.clients.splice(evt.newIndex, 0, movedItem)

      const statusToUpdate = prevGroups.find((g) => g.uid === toGroupUid)

      if (!statusToUpdate) {
        console.error('Status not found for group:', toGroupUid)
        return [...prevGroups]
      }

      debounceUpdate<UpdateClientType>({
        updateFn: (data) => clientStatusMutation.mutate(data),
        callId: itemId,
        dataFunction: {
          uid: itemId,
          status: statusToUpdate,
        },
      })

      return [...prevGroups]
    })
  }
}

export default Dashboard
